import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import AdminLogin from './components/AdminLogin';
import AdminPanel from './components/AdminPanel';
import './App.css';

// Process Step Component - Simplified for carousel
function ProcessStep({ step, index }) {
  return (
    <div className="process-step">
      <div className="step-number">
        {step.number}
      </div>
      <div className="step-content">
        <h3 className="step-title">{step.title}</h3>
        <p className="step-description">{step.description}</p>
      </div>
    </div>
  );
}

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const { scrollYProgress } = useScroll();
  const processRef = useRef(null);
  
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.2], [1, 0.9]);
  
  // Carousel state management
  const [carouselPosition, setCarouselPosition] = useState(0); // 0 to 936px (3 cards * (280px + 32px gap))
  const [isInProcessSection, setIsInProcessSection] = useState(false);
  
  // Track if we're in the process section
  useEffect(() => {
    const handleScroll = () => {
      if (!processRef.current) return;
      
      const rect = processRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Check if process section is in viewport (top is at or above top of screen)
      const isVisible = rect.top <= 100 && rect.bottom >= windowHeight - 100;
      setIsInProcessSection(isVisible);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Hijack scroll when in process section
  useEffect(() => {
    const handleWheel = (e) => {
      if (!isInProcessSection) return;
      
      const delta = e.deltaY;
      
      // If scrolling down and carousel not finished, hijack scroll
      if (delta > 0 && carouselPosition < 936) {
        e.preventDefault();
        e.stopPropagation();
        const newPosition = Math.min(936, carouselPosition + delta * 0.5);
        setCarouselPosition(newPosition);
        return false;
      }
      
      // If scrolling up and carousel position > 0, hijack scroll
      if (delta < 0 && carouselPosition > 0) {
        e.preventDefault();
        e.stopPropagation();
        const newPosition = Math.max(0, carouselPosition + delta * 0.5);
        setCarouselPosition(newPosition);
        return false;
      }
      
      // Otherwise allow normal scroll (carousel at start or end)
    };
    
    let touchStartY = 0;
    let lastTouchY = 0;
    
    const handleTouchStart = (e) => {
      if (!isInProcessSection) return;
      touchStartY = e.touches[0].clientY;
      lastTouchY = touchStartY;
    };
    
    const handleTouchMove = (e) => {
      if (!isInProcessSection) return;
      
      const touchY = e.touches[0].clientY;
      const delta = lastTouchY - touchY;
      
      // If swiping up and carousel not finished, hijack scroll
      if (delta > 0 && carouselPosition < 936) {
        e.preventDefault();
        e.stopPropagation();
        const newPosition = Math.min(936, carouselPosition + delta * 0.5);
        setCarouselPosition(newPosition);
        lastTouchY = touchY;
        return false;
      }
      
      // If swiping down and carousel position > 0, hijack scroll
      if (delta < 0 && carouselPosition > 0) {
        e.preventDefault();
        e.stopPropagation();
        const newPosition = Math.max(0, carouselPosition + delta * 0.5);
        setCarouselPosition(newPosition);
        lastTouchY = touchY;
        return false;
      }
      
      lastTouchY = touchY;
    };
    
    // Always add listeners but they check isInProcessSection internally
    document.addEventListener('wheel', handleWheel, { passive: false });
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    
    return () => {
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isInProcessSection, carouselPosition]);
  
  // Icon animations removed for mobile focus

  // Dynamic content from Firestore
  const [galleryImages, setGalleryImages] = useState([]);
  const [musicItems, setMusicItems] = useState([]);
  const [aboutText, setAboutText] = useState({ paragraph1: '', paragraph2: '' });

  // Process steps
  const processSteps = [
    {
      number: 1,
      title: "Introduction and Assessment",
      description: "In this stage both your business and our agency talk for the first time and choose whether we'll work together or not."
    },
    {
      number: 2,
      title: "Deep Analysis",
      description: "In this step, our expert team devotes an extended amount of time to research any information about your business and its competitors to ensure the next steps are efficient, relevant and cost effective for both your business and our agency."
    },
    {
      number: 3,
      title: "Production",
      description: "At this point we already know our Deep Analysis stage's results and have already prepared tailored scripts for the videos your business needs. In this stage we shoot those videos according to the terms in our agreement."
    },
    {
      number: 4,
      title: "Delivery",
      description: "After the whole process, from the small spark that set our first conversation and up until the final visual/sound effect in your videos, in this stage you will get your money's worth with a set amount of polished, tailored, professional videos for your business's use."
    }
  ];

  useEffect(() => {
    // Check if we're on admin route
    const checkAdminRoute = () => {
      const hash = window.location.hash;
      setIsAdmin(hash === '#admin');
    };

    checkAdminRoute();
    window.addEventListener('hashchange', checkAdminRoute);

    return () => window.removeEventListener('hashchange', checkAdminRoute);
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      loadFirestoreData();
    }
  }, [isAdmin]);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'process', 'gallery', 'contact'];
      const scrollPosition = window.scrollY + window.innerHeight / 2;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadFirestoreData = async () => {
    try {
      // Load gallery
      const gallerySnapshot = await getDocs(collection(db, 'gallery'));
      const gallery = gallerySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // If no data, use placeholder
      if (gallery.length > 0) {
        setGalleryImages(gallery.sort((a, b) => (a.order || 0) - (b.order || 0)));
      } else {
        // Default gallery images
        setGalleryImages([
          { id: 1, url: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=800', title: 'Performance 1' },
          { id: 2, url: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800', title: 'Studio Session' },
          { id: 3, url: 'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=800', title: 'Live Show' },
          { id: 4, url: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=800', title: 'Recording' },
          { id: 5, url: 'https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?w=800', title: 'Performance 2' },
          { id: 6, url: 'https://images.unsplash.com/photo-1506157786151-b8491531f063?w=800', title: 'Music Video' },
          { id: 7, url: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800', title: 'Concert' },
          { id: 8, url: 'https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=800', title: 'Backstage' },
        ]);
      }

      // Load music
      const musicSnapshot = await getDocs(collection(db, 'music'));
      const music = musicSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      if (music.length > 0) {
        setMusicItems(music.sort((a, b) => (a.order || 0) - (b.order || 0)));
      } else {
        // Default music items
        setMusicItems([
          { title: 'Latest Album', subtitle: 'Echoes of Tomorrow', year: '2025', link: '' },
          { title: 'Featured Track', subtitle: 'Neon Dreams', year: '2024', link: '' },
          { title: 'Live Session', subtitle: 'Acoustic Series', year: '2024', link: '' }
        ]);
      }

      // Load about
      const aboutSnapshot = await getDocs(collection(db, 'about'));
      if (!aboutSnapshot.empty) {
        const aboutData = aboutSnapshot.docs[0].data();
        setAboutText(aboutData);
      } else {
        // Default about text
        setAboutText({
          paragraph1: "As a 3-year major in Industrial Management and Economics, Yali's goal when creating Molaga Studios was making sure the marketing and videos of a business weren't \"accidental success\"; but rather a calculated step on the way to a flourishing professional business.",
          paragraph2: "A quick overview would look like:"
        });
      }
    } catch (error) {
      console.error('Error loading data from Firestore:', error);
      // Use default data on error
      setGalleryImages([
        { id: 1, url: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=800', title: 'Performance 1' },
        { id: 2, url: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800', title: 'Studio Session' },
        { id: 3, url: 'https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=800', title: 'Live Show' },
        { id: 4, url: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=800', title: 'Recording' },
        { id: 5, url: 'https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?w=800', title: 'Performance 2' },
        { id: 6, url: 'https://images.unsplash.com/photo-1506157786151-b8491531f063?w=800', title: 'Music Video' },
        { id: 7, url: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800', title: 'Concert' },
        { id: 8, url: 'https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=800', title: 'Backstage' },
      ]);
      setMusicItems([
        { title: 'Latest Album', subtitle: 'Echoes of Tomorrow', year: '2025', link: '' },
        { title: 'Featured Track', subtitle: 'Neon Dreams', year: '2024', link: '' },
        { title: 'Live Session', subtitle: 'Acoustic Series', year: '2024', link: '' }
      ]);
      setAboutText({
        paragraph1: "As a 3-year major in Industrial Management and Economics, Yali's goal when creating Molaga Studios was making sure the marketing and videos of a business weren't \"accidental success\"; but rather a calculated step on the way to a flourishing professional business.",
        paragraph2: "A quick overview would look like:"
      });
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleAdminLogin = () => {
    setIsAdminLoggedIn(true);
  };

  const handleAdminLogout = () => {
    setIsAdminLoggedIn(false);
    window.location.hash = '';
  };

  // Show admin panel if on admin route
  if (isAdmin) {
    if (!isAdminLoggedIn) {
      return <AdminLogin onLogin={handleAdminLogin} />;
    }
    return <AdminPanel onLogout={handleAdminLogout} />;
  }

  return (
    <div className="App">
      {/* Navigation */}
      <motion.nav 
        className="nav"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="nav-content">
          <motion.div 
            className="logo"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            MOLAGA STUDIOS
          </motion.div>
          
          <button 
            className="menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <ul className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
            {[
              { id: 'home', label: 'Home' },
              { id: 'about', label: 'About' },
              { id: 'process', label: 'Process' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'contact', label: 'Contact' }
            ].map((item) => (
              <motion.li 
                key={item.id}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <button 
                  className={activeSection === item.id ? 'active' : ''}
                  onClick={() => scrollToSection(item.id)}
                >
                  {item.label}
                </button>
              </motion.li>
            ))}
          </ul>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <motion.div 
          className="hero-content"
          style={{ opacity: heroOpacity, scale: heroScale }}
        >
          <div className="hero-3d-container">
            <motion.div 
              className="hero-3d-card"
              animate={{ 
                rotateY: [0, 10, -10, 0],
                rotateX: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <div className="card-face front">
                <h1 className="hero-title">
                  <span className="title-line">MOLAGA</span>
                  <span className="title-line">STUDIOS</span>
                </h1>
              </div>
            </motion.div>
          </div>
          
          <motion.p 
            className="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            Marketing & Video Production Agency • Los Angeles
          </motion.p>

          <motion.div 
            className="hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <motion.button 
              className="cta-button primary"
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(255,255,255,0.2)" }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('process')}
            >
              Our Process
            </motion.button>
            <motion.button 
              className="cta-button secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('contact')}
            >
              Get Started
            </motion.button>
          </motion.div>
        </motion.div>

        <div className="hero-bg-elements">
          <motion.div 
            className="floating-element el-1"
            animate={{ 
              y: [0, -30, 0],
              rotate: [0, 180, 360]
            }}
            transition={{ 
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div 
            className="floating-element el-2"
            animate={{ 
              y: [0, 40, 0],
              rotate: [0, -180, -360]
            }}
            transition={{ 
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div 
            className="floating-element el-3"
            animate={{ 
              y: [0, -20, 0],
              x: [0, 20, 0]
            }}
            transition={{ 
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <motion.div 
          className="glass-card"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            What Makes Us Different?
          </motion.h2>
          <motion.p 
            className="about-text"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            As a 3-year major in Industrial Management and Economics, Yali's goal when creating Molaga Studios was making sure the marketing and videos of a business weren't "accidental success"; but rather a calculated step on the way to a flourishing professional business.
          </motion.p>
          <motion.div 
            className="about-overview"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
          >
            <p className="about-text">A quick overview would look like:</p>
          </motion.div>
        </motion.div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="gallery">
        <motion.h2 
          className="section-title"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Gallery
        </motion.h2>
        
        <div className="gallery-grid">
          {galleryImages.map((image, index) => (
            <motion.div
              key={image.id}
              className="gallery-item"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ 
                scale: 1.05,
                zIndex: 10,
                transition: { duration: 0.3 }
              }}
              viewport={{ once: true }}
            >
              <div className="gallery-item-inner">
                <img src={image.url} alt={image.title} />
                <motion.div 
                  className="gallery-overlay"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                >
                  <h3>{image.title}</h3>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="process" ref={processRef}>
        <motion.h2 
          className="section-title process-title"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          style={{
            position: 'absolute',
            top: '2rem',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10
          }}
        >
          Our Process
        </motion.h2>

        <motion.div 
          className="process-timeline"
          animate={{
            x: -carouselPosition
          }}
          transition={{
            type: "tween",
            duration: 0.1,
            ease: "linear"
          }}
        >
          {processSteps.map((step, index) => (
            <ProcessStep 
              key={step.number}
              step={step}
              index={index}
            />
          ))}
        </motion.div>
        
        {/* Scroll progress indicator */}
        {carouselPosition > 0 && carouselPosition < 936 && (
          <motion.div 
            className="scroll-indicator"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: '50%',
              right: '20px',
              transform: 'translateY(-50%)',
              background: 'rgba(168, 213, 255, 0.3)',
              height: '200px',
              width: '4px',
              borderRadius: '2px',
              zIndex: 1000
            }}
          >
            <motion.div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(180deg, var(--blue-pastel), var(--blue-dark))',
                height: `${(carouselPosition / 936) * 100}%`,
                borderRadius: '2px'
              }}
            />
          </motion.div>
        )}
        
        {/* Active scroll hijacking indicator */}
        {isInProcessSection && carouselPosition > 0 && carouselPosition < 936 && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              bottom: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(168, 213, 255, 0.2)',
              padding: '12px 24px',
              borderRadius: '20px',
              color: 'var(--blue-pastel)',
              fontSize: '0.9rem',
              zIndex: 1000,
              backdropFilter: 'blur(10px)'
            }}
          >
            ⬅️ גלול להצגת כל השלבים ➡️
          </motion.div>
        )}
        
        {/* Carousel completion indicator */}
        {carouselPosition >= 936 && isInProcessSection && (
          <motion.div 
            className="completion-indicator"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              position: 'fixed',
              bottom: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(168, 213, 255, 0.2)',
              padding: '12px 24px',
              borderRadius: '20px',
              color: 'var(--blue-pastel)',
              fontSize: '0.9rem',
              zIndex: 1000,
              backdropFilter: 'blur(10px)'
            }}
          >
            ↓ גלול למטה להמשך
          </motion.div>
        )}
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <motion.div 
          className="glass-card contact-card"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Let's Connect</h2>
          <p className="contact-text">
            Interested in collaborating or booking a performance? 
            I'd love to hear from you.
          </p>
          
          <div className="contact-methods">
            <motion.a 
              href="mailto:yali@molaga.com"
              className="contact-link"
              whileHover={{ scale: 1.05, x: 10 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="icon">📧</span>
              <span>yali@molaga.com</span>
            </motion.a>
            <motion.a 
              href="https://instagram.com/yalimolaga"
          target="_blank"
          rel="noopener noreferrer"
              className="contact-link"
              whileHover={{ scale: 1.05, x: 10 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="icon">📱</span>
              <span>@yalimolaga</span>
            </motion.a>
            <motion.div 
              className="contact-link"
              whileHover={{ scale: 1.05, x: 10 }}
            >
              <span className="icon">📍</span>
              <span>Los Angeles, CA</span>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p>&copy; 2025 Molaga Studios. All rights reserved.</p>
        <a href="#admin" className="admin-link">Admin</a>
      </footer>
    </div>
  );
}

export default App;
